'use client';

import { useCallback, useEffect, useState } from 'react';
import type { DeviceStorage } from '../deviceStorage';

/*
  A board too wide for a phone (Mosaic's biggest) is either panned at playing size or zoomed out to show all of it,
  and double tapping its minimap switches between the two. It starts zoomed out, and after that as the player last
  left it. Until they've switched once, the minimap carries a tip saying how: on every puzzle, though any touch puts
  it away for that one.
*/

/** A board that can pan is either panning at playing size, or zoomed out to show all of it. */
export type Zoom = 'in' | 'out';

/** Where the device keeps the zoom the player last chose. */
export const BOARD_ZOOM_KEY = 'board_zoom';

export const ZOOM_TIP = 'Double tap here to zoom in.';

// What each storage holds, once read: the read is slow in the app, so it's made once and kept.
const remembered = new WeakMap<DeviceStorage, Zoom | null>();
const reads = new WeakMap<DeviceStorage, Promise<Zoom | null>>();

/**
 * Reads the zoom the player last chose, null if they never have. A client whose storage is slow calls this before
 * any board can open, so boards start at the right zoom rather than switching to it once the read lands.
 */
export function preloadBoardZoom(storage: DeviceStorage): Promise<Zoom | null> {
  let read = reads.get(storage);
  if (!read) {
    read = Promise.resolve()
      .then(() => storage.get(BOARD_ZOOM_KEY))
      .then(
        (value) => (value === 'in' || value === 'out' ? value : null),
        () => null
      )
      .then((zoom) => {
        if (!remembered.has(storage)) remembered.set(storage, zoom);
        return remembered.get(storage) ?? null;
      });
    reads.set(storage, read);
  }
  return read;
}

/**
 * The zoom to show, the switch between the two, whether to show the tip, and what puts the tip away. The tip
 * waits for storage to be read, so it never flashes up for a player who has chosen; storage that can't be read
 * counts as never having chosen.
 */
export function useBoardZoom(storage: DeviceStorage) {
  // Undefined until storage has been read, null if the player has never chosen.
  const [chosen, setChosen] = useState<Zoom | null | undefined>(() => remembered.get(storage));
  useEffect(() => {
    let live = true;
    preloadBoardZoom(storage).then((zoom) => live && setChosen((current) => (current === undefined ? zoom : current)));
    return () => {
      live = false;
    };
  }, [storage]);

  const [dismissed, setDismissed] = useState(false);
  const dismissTip = useCallback(() => setDismissed(true), []);

  const zoom: Zoom = chosen ?? 'out';
  const toggleZoom = useCallback(() => {
    const next = zoom === 'in' ? 'out' : 'in';
    setChosen(next);
    remembered.set(storage, next);
    Promise.resolve()
      .then(() => storage.set(BOARD_ZOOM_KEY, next))
      .catch(() => {});
  }, [zoom, storage]);

  return { zoom, toggleZoom, showTip: chosen === null && !dismissed, dismissTip };
}
